<template>
	<div id="app" class="text-dark">
		<template v-if="clientConfig && profile">
			<div id="header" class="shadow-sm sticky-top">
				<transition key="transition.header" name="fade" appear>
					<b-navbar ref="mainHeader" tag="header" class="w-100" sticky type="light" variant="light">
						<b-navbar-brand to="/"><img :src="logo" style="height: 48px;" :alt="clientConfig.name" v-if="clientConfig && clientConfig.name"></b-navbar-brand>
						<b-navbar-nav class="ml-auto">
							<b-nav-text class="mb-0 pb-0"><transition name="fade" appear><h3 v-if="profile"><b>{{ profile.institution.name || profile.institution }}</b> | {{ profile.firstName }} {{ profile.lastName }}</h3></transition></b-nav-text>
						</b-navbar-nav>
					</b-navbar>
				</transition>
			</div>
			<div id="body">
				<template v-if="profile && enrollments">
					<transition key="transition.main-navigation" name="fade" appear>
						<div id="main-navigation" key="main-navigation" class="bg-dark text-light">
							<b-list-group class="w-100 mt-4 text-left text-light bg-dark" style="min-height: calc(100% - 72px); max-height: calc(100% - 72px); overflow: auto;" flush>
								<b-list-group-item class="m-0 p-0 text-white-50 bg-dark">
									<b-row no-gutters class="w-100">
										<b-col class="m-0 p-0 w-100">
											<h6 class="ml-2 my-3 p-0 text-truncate"><strong>Courses</strong></h6>
										</b-col>
									</b-row>
								</b-list-group-item>
								<NavItem :item="item" class="m-0 p-0" v-for="item in enrollments" :key="item.uuid" />
							</b-list-group>
							<div id="powered-by-elumin">
								<div id="powered-by-notice" class="text-white text-left pr-4" :class="(!shouldShowPoweredBy) ? 'd-none': ''" :style="'opacity: ' + 0.5 + ';'">
									<span class="align-middle"><span class="powered-by-text">Powered by</span><br/><a target="_blank" href="https://elumin.co"><img :src="require(`@/assets/elumin-logo-white.svg`)" alt="eLumin"></a></span>
								</div>
							</div>
						</div>
					</transition>
					<transition key="transition.main-content" name="fade" appear>
						<div id="main-content" key="main-content">
							<router-view ref="view" :key="$route.fullPath" />
						</div>
					</transition>
				</template>
				<elumin-loader v-else />
			</div>
			<div id="footer" class="mx-auto bg-dark text-secondary text-center">
				<div>
					<span id="copyright-notice" class="align-middle" :class="(!shouldShowCopyrightFooter) ? 'd-none': ''">&copy; {{ (new Date()).getFullYear() }}, eLumin LLC, All rights reserved.</span>
					<span class="align-middle">v{{ build.version }} ({{ build.date }})</span>
				</div>
			</div>
		</template>
		<div style="min-height: 100%; display: flex;" v-else>
			<div class="w-100 m-auto text-center">
				<h3>
					<span v-if="!clientConfig">Loading...</span>
					<span v-else-if="!profile">Getting your profile...</span>
				</h3>
				<elumin-loader />
			</div>
		</div>
		<ErrorModal :ref="errorModalData.id" :modalData="errorModalData" />
	</div>
</template>

<script>
	import Vue from 'vue';
	import VueMeta from 'vue-meta';
	import VueTimers from 'vue-timers';
	
	import { mapMutations, mapState } from 'vuex';
	import { library } from '@fortawesome/fontawesome-svg-core';
	import { faCaretDown, faCaretRight, faQuestion, faPlayCircle } from '@fortawesome/free-solid-svg-icons';
	
	import NavItem from '@/components/shared/NavItem';
	
	Vue.use(
		VueMeta,
		{
			refreshOnceOnNavigation: true
		}
	);
	Vue.use(VueTimers);
	
	library.add(faCaretDown, faCaretRight, faQuestion, faPlayCircle);
	
	let listeners = [];
	
	export default
	{
		components: { NavItem },
		name: 'app',
		metaInfo()
		{
			if(!this.clientConfig)
				return { title: 'Learning Lab' };
			
			const title = ((this.whiteLabelTitle) ? '' : 'eLumin');
			
			const link = [];
			
			if(!this.whiteLabelTitle)
			{
				link.push(
					{
						rel: 'icon',
						type: 'image/png',
						sizes: '32x32',
						href: 'img/ico/eLumin/favicon-32x32.png?v=20190507.001'
					},
					{
						rel: 'icon',
						type: 'image/png',
						sizes: '16x16',
						href: 'img/ico/eLumin/favicon-16x16.png?v=20190507.001'
					},
					{
						rel: 'shortcut icon',
						type: 'image/png',
						href: 'img/ico/eLumin/favicon-32x32.png?v=20190507.001'
					}
				);
			}
			else
			{
				link.push(
					{
						rel: 'icon',
						type: 'image/png',
						sizes: '32x32',
						href: 'img/ico/generic/favicon-32x32.png?v=20190507.001'
					},
					{
						rel: 'icon',
						type: 'image/png',
						sizes: '16x16',
						href: 'img/ico/generic/favicon-16x16.png?v=20190507.001'
					},
					{
						rel: 'shortcut icon',
						type: 'image/png',
						href: 'img/ico/generic/favicon-32x32.png?v=20190507.001'
					}
				);
			}
			
			return {
				title,
				titleTemplate: '%s Learning Lab',
				link
			};
		},
		computed:
		{
			...mapState([ 'clientKey', 'clientConfig', 'loadedAt', 'shouldShowTooltips' ]),
			
			appName: function()
			{
				return (window.location.hostname != 's3.amazonaws.com') ? window.location.hostname.split('.')[0] : (new URL(window.location.href)).pathname.split('/')[2];
			},
			apiURL: function()
			{
				return (process.env.VUE_APP_CLIENT_API || this.$store.state.clientConfig.apiURLs.sandbox) + '/ck:' + this.$store.state.clientKey;
			},
			apiCoreURL: function()
			{
				return process.env.VUE_APP_ROOT_API + '/ck:' + this.$store.state.clientKey;
			},
			build: function()
			{
				return { version: _ELUMIN_BUILD_VERSION, date: _ELUMIN_BUILD_DATE };
			},
			logo: function()
			{
				let logo = undefined;
				
				try
				{
					logo = 'https://s3.amazonaws.com/co.elumin.sites.prd/config/' + this.clientKey[0] + '/' + this.clientKey[1] + '/' + this.clientKey + '/logo.png'
				}
				catch(e)
				{
					logo = require('@/assets/elumin.svg');
				}
				
				return logo;
			},
			whiteLabelTitle: function()
			{
				return (this.clientConfig && ('whiteLabel' in this.clientConfig) && this.clientConfig.whiteLabel.title);
			},
			shouldShowPoweredBy: function()
			{
				if(this.clientConfig && ('whiteLabel' in this.clientConfig) && ('poweredBy' in this.clientConfig.whiteLabel))
					return !this.clientConfig.whiteLabel.poweredBy;
				
				return true;
			},
			shouldShowCopyrightFooter: function()
			{
				if(this.clientConfig && ('whiteLabel' in this.clientConfig) && ('copyrightFooter' in this.clientConfig.whiteLabel))
					return !this.clientConfig.whiteLabel.copyrightFooter;
				
				return true;
			}
		},
		asyncComputed:
		{
			enrollments:
			{
				lazy: true,
				get()
				{
					if(!this.profile)
						return undefined;
					
					return fetch(
						this.apiURL + '/enrollments',
						{
							method: 'get',
							headers: { 'Content-type': 'application/json' },
							credentials: 'include'
						}
					).then(
						(res) => res.json(),
						(err) =>
						{
							console.error(err);
						}
					).then(
						(data) => [ 
							...data
								.filter(d => /^BUILDER\./.test(d.courseId)),
							...data
								.filter(d => !/^BUILDER\./.test(d.courseId))
								.sort((a, b) => { return (a.courseId > b.courseId) ? 1 : (a.courseId < b.courseId) ? -1 : 0 })
								
						].map((d) => { d.courseId = d.courseId.replace('.', ' '); return d; })
					);
				}
			}
		},
		methods:
		{
			toggleTooltips: function(v)
			{
				this.$store.commit(
					'shouldShowTooltips',
					((v !== undefined) ? Boolean(v) : !this.shouldShowTooltips)
				);
				
				console.debug('Tooltip status: ' + this.shouldShowTooltips);
			},
			init: async function()
			{
				console.log('Copyright (C) {{ (new Date()).getFullYear() }} eLumin LLC - All Rights Reserved. Unauthorized copying of this application, via any medium is strictly prohibited. Proprietary and confidential.');
				console.log('Initializing eLumin ' + this.appName.replace(/^./, (c) => c.toUpperCase()) + '...');
				
				this.toggleTooltips(false);
				
				if(/^Init/.test(this.$route.name))
				{
					let params = this.$route.params;
					
					if(('clientKey' in params) || ('userToken' in params))
					{
						if(params.clientKey)
						{
							console.log('Found clientKey: ' + params.clientKey);
							
							this.$store.commit('clientKey', params.clientKey);
						}
						
						if(params.userToken)
						{
							console.log('Found userToken: ' + params.userToken);
							
							this.$store.commit('userToken', params.userToken);
						}
					}
				}
				
				if(!this.$store.state.userToken)
					return false;
				
				let clientConfig = await fetch(
					this.apiCoreURL + '/client',
					{
						method: 'get',
						headers: { 'Content-type': 'application/json' },
						credentials: 'include'
					}
				).then(
					(res) => res.json(),
					(err) =>
					{
						console.error(err);
					}
				).catch(e => this.showError(e));
				
				if(!clientConfig || clientConfig.status != 'OK')
				{
					this.showError(new Error('There was an error setting up the application. Please contact support if this error persists.'));
					
					return;
				}
				
				this.$store.commit('clientConfig', clientConfig.config);
				
				let profile = await fetch(
					this.apiURL + '/auth/session/start',
					{
						method: 'post',
						body: atob(this.$store.state.userToken),
						headers: { 'Content-type': 'application/json' },
						credentials: 'include'
					}
				).then(
					async (res) =>
					{
						let result = await res.json();
						
						if(!res.ok && ('message' in result))
						{
							this.showError(result.message);
						}
				
 						return result;
					},
					(err) =>
					{
						this.showError(err.message);
						
						return;
					}
				).catch(e => this.showError(e));
				
				this.$store.commit('profile', profile.profile);
				
				// There is something odd with using profile from store
				this.profile = profile.profile;
				
				if(/^init/i.test(this.$route.name))
					this.$router.push({ path: '/' });
				
				this.$store.commit('loadedAt', new Date());
			},
			showError: function(message)
			{
				let data = this.errorModalData;
				
				console.error(message);
				data.message = message;
				
				this.$root.$emit('bv::show::modal', data.id);
				
				return false;
			},
			slugify: function(text)
			{
				return text.toString().toLowerCase()
					.replace(/\s+/g, '-')  
					.replace(/[^\w\-]+/g, '') 
					.replace(/\-\-+/g, '-')  
					.replace(/^-+/, '')  
					.replace(/-+$/, ''); 
			}
		},
		mounted()
		{
			let vm = this;
			
			this.$nextTick(this.init);
		},
		beforeDestroy()
		{
			listeners.forEach(
				(v) =>
				{
					console.debug('Deregistering listener: ' + v);
					
					this.$root.$off(v);
				}
			);
		},
		watch:
		{
			'$route.params': function(params)
			{
				if(/^Init/.test(this.$route.name))
					this.$nextTick(this.init);
			}
		},
		timers:
		{
			refreshApp:
			{
				callback: function()
				{
					if((new Date() - this.loadedAt) > (1000 * 60 * 60))
						this.$router.go();
				},
				autostart: true,
				repeat: true,
				immediate: false,
				time: 1000 * 60 * 5
			}
		},
		data()
		{
			return {
				profile: undefined,
				errorModalData:
				{
					id: 'errorModal',
					message: undefined,
				}
			}
		}
	}
</script>

<style>
	@import url('https://fonts.googleapis.com/css2?family=Lato:wght@400;700;900&display=swap');
	
	#app
	{
		font-family: "Lato", sans-serif;
		height: 100%;
		min-height: 100%;
		max-height: 100%;
	}
	
	#header
	{
		min-height: 64px;
		max-height: 64px;
		
		min-width: 100%;
	}
	
	#header header
	{
		padding: 0 20px;
		min-height: 64px;
	}
	
	#body
	{
		min-height: calc(100% - 96px);
		max-height: calc(100% - 96px);
		
		min-width: 100%;
		
		overflow: auto;
		
		display: flex;
	}
	
	#main-navigation
	{
		min-height: 100%;
		max-height: 100%;

		max-width: 256px;
		min-width: 256px;
		
		overflow: auto;
		
		padding-top: 12px;
		
		display: flex; 
		flex-wrap: wrap;
	}
	
	#main-content
	{
		max-height: 100%;
		
		width:100%;
		max-width: calc(100% - 256px);
		
		overflow: auto;
		
		padding: 12px;
	}
		
	#alert-anchor
	{
		max-width:400px;
		position: fixed;
		z-index: 99999;
		top: 100px;
		right: 30px;
	}
	
	#footer
	{
		position: relative;
		
		min-height: 32px;
		max-height: 32px;
		
		min-width: 100%;
		
		overflow: visible;
		
		line-height: 30px;
		
		font-size: 12px;
	}

	#powered-by-elumin {
		width:100%; 
		margin-top:auto;
		padding-left:20px;
	}

	#powered-by-notice
	{
		margin-top: auto;
		margin-left: auto;
		margin-right: auto;
		
		line-height: initial;
	}
	
	.navbar-text
	{
		color: var(--dark) !important;
	}
	
	.btn-dark:hover:not(:disabled), .btn-dark-active
	{
		background-color: var(--blue) !important;
		border-color: var(--blue) !important;
	}
	
	.fade-enter-active, .fade-leave-active
	{
		transition: all 0.25s;
	}
	
	.fade-enter, .fade-leave-to
	{
		opacity: 0;
	}
	
	.powered-by-text {
		margin-left:28px;
		font-size:14px;
		position: relative;
		bottom: 3px
	}
</style>
